/**
 * BatchComposer — multi-prompt batch analysis UI (PRI-488 F7).
 *
 * Dogfoods POST /v1/analyses/batch end-to-end through the public SDK:
 *   * live dry-run pricing via validateBatch (validate_only: true — no credits)
 *   * "1 × full + 4 × 50% = 3.0 credits, saving 2.0" breakdown
 *   * createBatch on Run, then polls each analysis via GET /v1/analyses/{id}
 *     until terminal and renders the same answer semantics as the results UI
 *     (detected_count / indeterminate_reason via formatAnswerLine).
 *
 * Self-contained: owns all batch state; the host only supplies the public
 * video id (video_…) captured at upload time. 2–10 prompts, first full price,
 * each additional at 50% (server-enforced; mirrored in copy only).
 */
import { useEffect, useRef, useState } from 'react';
import { L } from './pv2-shared';
import { sdk } from '../../lib/api/sdk';
import { formatAnswerLine } from '../../lib/result-semantics';
import { trackBatchAnalysisSubmitted } from '../../telemetry/events';
import type { AnalysisBatchPreview, Analysis } from '@primate-intelligence/sdk/browser';

const MAX_PROMPTS = 10;
const POLL_MS = 2000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * Human price breakdown from a batch preview (pure — unit-tested).
 * Credits are billed seconds (1 credit = 1s).
 */
export function formatBatchBreakdown(preview: AnalysisBatchPreview): {
  line: string
  totalSeconds: number | null
  savingSeconds: number | null
} {
  const { pricing, prompts } = preview;
  const full = pricing.full_price_prompts;
  const disc = pricing.discounted_prompts;
  const total = pricing.estimated_total_seconds;
  if (total == null) {
    return {
      line: `${full} × full + ${disc} × ${pricing.discount_pct}% — cost known once video duration is probed`,
      totalSeconds: null,
      savingSeconds: null,
    };
  }
  // Undiscounted cost = what (full+disc) prompts would cost at full price.
  // Derive the full-price per-prompt seconds from the first full-price row.
  const fullRow = prompts.find(p => p.discount_pct === 0);
  const perPromptFull = fullRow?.estimated_seconds ?? null;
  const saving = perPromptFull != null
    ? perPromptFull * (full + disc) - total
    : null;
  const line = `${full} × full + ${disc} × ${pricing.discount_pct}% = ${total} credit${total === 1 ? '' : 's'}`
    + (saving != null && saving > 0 ? `, saving ${saving}` : '');
  return { line, totalSeconds: total, savingSeconds: saving };
}

type BatchAnalysisRow = {
  id: string
  prompt: string
  status: Analysis['status']
  resultLine: string | null
  failed: boolean
}

export function BatchComposer({ publicVideoId, onClose, userPlan = 'free' }: {
  publicVideoId: string | null
  onClose: () => void
  userPlan?: string
}) {
  const [prompts, setPrompts] = useState<string[]>(['', '']);
  const [preview, setPreview] = useState<AnalysisBatchPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [rows, setRows] = useState<BatchAnalysisRow[]>([]);
  const genRef = useRef(0);
  const pollersRef = useRef<ReturnType<typeof setInterval>[]>([]);

  const filled = prompts.map(p => p.trim()).filter(Boolean);
  const canPreview = publicVideoId != null && filled.length >= 2;
  const canRun = canPreview && !running && rows.length === 0;

  // Debounced dry-run pricing (validate_only — zero credits, zero jobs).
  useEffect(() => {
    const gen = ++genRef.current;
    setPreviewError(null);
    if (!canPreview) { setPreview(null); return; }
    const t = setTimeout(() => {
      sdk.analyses.validateBatch({ video_id: publicVideoId!, prompts: filled })
        .then(p => { if (genRef.current === gen) setPreview(p); })
        .catch((e: unknown) => {
          if (genRef.current !== gen) return;
          setPreview(null);
          setPreviewError(e instanceof Error ? e.message : 'Could not price this batch.');
        });
    }, 500);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicVideoId, prompts.map(p => p.trim()).join('\u0000')]);

  // Cleanup pollers on unmount.
  useEffect(() => () => { pollersRef.current.forEach(clearInterval); }, []);

  function analysisToLine(a: Analysis): string | null {
    if (a.status === 'failed') return a.error?.message ?? 'Analysis failed.';
    if (a.status === 'canceled') return 'Canceled.';
    if (!a.result) return null;
    return formatAnswerLine({
      answer: a.result.answer ?? null,
      detectedCount: a.result.detected_count ?? null,
      indeterminateReason: a.result.indeterminate_reason ?? null,
    });
  }

  async function handleRun() {
    if (!canRun || !publicVideoId) return;
    setRunning(true);
    setRunError(null);
    try {
      const batch = await sdk.analyses.createBatch({ video_id: publicVideoId, prompts: filled });
      trackBatchAnalysisSubmitted({ prompt_count: filled.length, user_plan: userPlan });
      setRows(batch.analyses.map(a => ({
        id: a.id,
        prompt: a.prompt ?? '',
        status: a.status,
        resultLine: analysisToLine(a),
        failed: a.status === 'failed',
      })));
      const deadline = Date.now() + POLL_TIMEOUT_MS;
      for (const a of batch.analyses) {
        const iv = setInterval(() => {
          if (Date.now() > deadline) { clearInterval(iv); return; }
          void sdk.analyses.retrieve(a.id).then(fresh => {
            setRows(prev => prev.map(r => r.id === fresh.id
              ? { ...r, status: fresh.status, resultLine: analysisToLine(fresh), failed: fresh.status === 'failed' }
              : r));
            if (fresh.status === 'completed' || fresh.status === 'failed' || fresh.status === 'canceled') {
              clearInterval(iv);
            }
          }).catch(() => { /* transient — next tick retries */ });
        }, POLL_MS);
        pollersRef.current.push(iv);
      }
    } catch (e) {
      setRunError(e instanceof Error ? e.message : 'Batch submit failed.');
    } finally {
      setRunning(false);
    }
  }

  const breakdown = preview ? formatBatchBreakdown(preview) : null;
  const unassessable = preview?.prompts.filter(p => !p.assessable) ?? [];
  const mono = '"JetBrains Mono", monospace';
  const inter = 'Inter, -apple-system, sans-serif';

  return (
    <div data-testid="batch-composer" style={{ margin: '0 10px 8px', border: `1px solid ${L.border}`, borderRadius: 8, background: L.bgSecondary, padding: '10px 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontFamily: inter, fontSize: 12, fontWeight: 600, color: L.textPrimary }}>
          Batch prompts <span style={{ color: L.textMuted, fontWeight: 400 }}>— one video, 2–10 questions, extras 50% off</span>
        </span>
        <button onClick={onClose} aria-label="Close batch composer" style={{ background: 'none', border: 'none', cursor: 'pointer', color: L.textMuted, padding: 2, lineHeight: 1 }}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
        </button>
      </div>

      {publicVideoId == null && (
        <p data-testid="batch-no-video" style={{ fontFamily: inter, fontSize: 12, color: L.textMuted, margin: '0 0 8px' }}>
          Upload a video first — batch pricing needs the uploaded video.
        </p>
      )}

      {rows.length === 0 ? (
        <>
          {prompts.map((p, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <span style={{ fontFamily: mono, fontSize: 10, color: L.textMuted, width: 26, flexShrink: 0 }}>
                {i === 0 ? 'full' : '-50%'}
              </span>
              <input
                data-testid={`batch-prompt-${i}`}
                value={p}
                placeholder={i === 0 ? 'Is there a person?' : 'How many people are walking?'}
                onChange={e => setPrompts(prev => prev.map((v, j) => j === i ? e.target.value : v))}
                style={{
                  flex: 1, fontFamily: inter, fontSize: 13, color: L.textPrimary,
                  background: L.bgSurface, border: `1px solid ${L.border}`, borderRadius: 6,
                  padding: '6px 10px', outline: 'none',
                }}
              />
              {prompts.length > 2 && (
                <button
                  data-testid={`batch-remove-${i}`}
                  onClick={() => setPrompts(prev => prev.filter((_, j) => j !== i))}
                  aria-label={`Remove prompt ${i + 1}`}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: L.textMuted, padding: 2 }}
                >
                  <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                </button>
              )}
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            {prompts.length < MAX_PROMPTS && (
              <button
                data-testid="batch-add-prompt"
                onClick={() => setPrompts(prev => [...prev, ''])}
                style={{ fontFamily: inter, fontSize: 12, fontWeight: 600, color: L.signal, background: 'none', border: `1px dashed ${L.border}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
              >
                + Add prompt
              </button>
            )}
            <div style={{ flex: 1 }} />
            <button
              data-testid="batch-run"
              onClick={() => void handleRun()}
              disabled={!canRun}
              style={{
                fontFamily: inter, fontSize: 12, fontWeight: 600, color: '#fff',
                background: canRun ? '#16A34A' : L.border, border: 'none', borderRadius: 6,
                padding: '6px 14px', cursor: canRun ? 'pointer' : 'not-allowed',
              }}
            >
              {running ? 'Submitting…' : 'Run batch'}
            </button>
          </div>
          {/* Live price breakdown (validate_only) */}
          <div style={{ marginTop: 8, minHeight: 16 }}>
            {breakdown && (
              <span data-testid="batch-price-breakdown" style={{ fontFamily: mono, fontSize: 10, color: L.textMuted }}>
                {breakdown.line}
              </span>
            )}
            {unassessable.length > 0 && (
              <div data-testid="batch-unassessable" style={{ fontFamily: mono, fontSize: 10, color: '#D97706', marginTop: 4 }}>
                ⚠ Prompt{unassessable.length === 1 ? '' : 's'} {unassessable.map(p => p.index + 1).join(', ')} can’t be scored — rephrase as yes/no or count questions
              </div>
            )}
            {previewError && (
              <div data-testid="batch-preview-error" style={{ fontFamily: mono, fontSize: 10, color: '#DC2626', marginTop: 4 }}>
                {previewError}
              </div>
            )}
            {runError && (
              <div data-testid="batch-run-error" style={{ fontFamily: mono, fontSize: 10, color: '#DC2626', marginTop: 4 }}>
                {runError}
              </div>
            )}
          </div>
        </>
      ) : (
        <div data-testid="batch-results">
          {rows.map(r => (
            <div key={r.id} data-testid="batch-result-row" style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '5px 0', borderTop: `1px solid ${L.border}` }}>
              <span style={{ fontFamily: inter, fontSize: 12, color: L.textPrimary, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                “{r.prompt}”
              </span>
              {r.resultLine != null ? (
                <span style={{ fontFamily: inter, fontSize: 12, fontWeight: 600, color: r.failed ? '#DC2626' : '#16A34A', flexShrink: 0, maxWidth: '55%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.resultLine}
                </span>
              ) : (
                <span style={{ fontFamily: mono, fontSize: 10, color: L.textMuted, flexShrink: 0 }}>
                  {r.status}…
                </span>
              )}
            </div>
          ))}
          <button
            data-testid="batch-new"
            onClick={() => { pollersRef.current.forEach(clearInterval); pollersRef.current = []; setRows([]); setPreview(null); }}
            style={{ marginTop: 8, fontFamily: inter, fontSize: 12, fontWeight: 600, color: L.textPrimary, background: L.bgSurface, border: `1px solid ${L.border}`, borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}
          >
            New batch
          </button>
        </div>
      )}
    </div>
  );
}
